import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { LANG_PREFIXES, stripLangPrefix } from './LangWrapper';

const SITE = 'https://cctvplanner.io';

/** All languages including English */
const ALL_LANGS = ['en', ...LANG_PREFIXES] as const;

/** Convert internal lang code to hreflang attribute value */
function toHreflang(lang: string): string {
  if (lang === 'pt-br') return 'pt-BR';
  if (lang === 'pt-pt') return 'pt-PT';
  return lang;
}

const ogLocaleMap: Record<string, string> = {
  en: 'en_US', pl: 'pl_PL', de: 'de_DE', es: 'es_ES',
  'pt-br': 'pt_BR', 'pt-pt': 'pt_PT', hi: 'hi_IN', id: 'id_ID', fil: 'fil_PH',
  tr: 'tr_TR', ar: 'ar_SA', vi: 'vi_VN', th: 'th_TH',
  ja: 'ja_JP', ko: 'ko_KR', fr: 'fr_FR', it: 'it_IT',
  uk: 'uk_UA', cs: 'cs_CZ', nl: 'nl_NL',
};

/**
 * Renders <Helmet> with hreflang, canonical, og:locale, and html lang
 * for the current page across all supported languages.
 *
 * Uses react-helmet-async so tags are captured during SSG (not useEffect).
 * Placed in RootLayout so it runs on every page.
 */
export default function HreflangTags() {
  const location = useLocation();
  // Strip lang prefix AND trailing slashes so all URLs are normalized
  // (matches the post-process canonical format)
  const basePath = stripLangPrefix(location.pathname).replace(/\/+$/, '');

  // Don't add hreflang for protected/non-translatable routes.
  // Match on exact path or "prefix + /" so "/cctv-design" does NOT also
  // skip sibling SEO landings like "/cctv-design-tool" / "-software".
  // /compare/sku/ is EN-only programmatic SKU comparison (Day-3 sprint) — no per-lang
  // routes exist, so emitting hreflang would point Google at 404s.
  const skipPrefixes = ['/auth', '/dashboard', '/cctv-design', '/checkout-success', '/api-docs', '/camera/', '/compare/sku/'];
  if (skipPrefixes.some(p => basePath === p || basePath.startsWith(p.endsWith('/') ? p : p + '/'))) {
    return null;
  }

  // Detect current language from URL (handles both /es and /es/)
  const currentLangPrefix = LANG_PREFIXES.find(
    p => location.pathname.startsWith(`/${p}/`) || location.pathname === `/${p}` || location.pathname === `/${p}/`
  );
  const currentLang = currentLangPrefix || 'en';

  // Build hreflang entries (no trailing slash, consistent with canonical)
  const hreflangs = ALL_LANGS.map(lang => ({
    hreflang: toHreflang(lang),
    href: lang === 'en'
      ? (basePath ? `${SITE}${basePath}` : SITE)
      : `${SITE}/${lang}${basePath}`,
  }));

  // Self-canonical: every page (including localized variants) points to
  // itself so Google indexes each language version instead of treating
  // non-English URLs as duplicates of the English root.
  const canonicalHref = currentLang === 'en'
    ? (basePath ? `${SITE}${basePath}` : SITE)
    : `${SITE}/${currentLang}${basePath}`;

  return (
    <Helmet>
      <html lang={currentLang} dir={currentLang === 'ar' ? 'rtl' : 'ltr'} />
        <meta property="og:locale" content={ogLocaleMap[currentLang] || 'en_US'} />
      <link rel="canonical" href={canonicalHref} />
      <meta property="og:url" content={canonicalHref} />
      {/* Hreflang alternates for all languages */}
      {hreflangs.map(({ hreflang, href }) => (
        <link key={hreflang} rel="alternate" hrefLang={hreflang} href={href} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={basePath ? `${SITE}${basePath}` : SITE} />
      {/* og:locale:alternate for other languages */}
      {ALL_LANGS.filter(l => l !== currentLang).map(lang => (
        <meta key={lang} property="og:locale:alternate" content={ogLocaleMap[lang] || lang} />
      ))}
    </Helmet>
  );
}
